import * as queryString from 'query-string';
import {
  getInstructionHubFlags,
  getWindowSearch
} from './instruction-hub-flags';

export const INSTRUCTION_HUB_ASSIGNMENT_FLAG = 'isInstructionHubAssignment';

function isQueryParamEnabled(search, paramName) {
  const params = queryString.parse(search);
  return params[paramName] === 'true';
}

export function shouldMarkInstructionHubAssignmentsFromQuery(search = '') {
  return isQueryParamEnabled(search, 'markInstructionHubAssignments');
}

export function shouldMarkInstructionHubResultsFromQuery(search = '') {
  return isQueryParamEnabled(search, 'markInstructionHubResults');
}

export function isInstructionHubAssignmentRecord(assignment, config) {
  return (
    config.isEnabled && assignment?.[INSTRUCTION_HUB_ASSIGNMENT_FLAG] === true
  );
}

export function isInstructionHubAssignment(studentAssignment, config) {
  return isInstructionHubAssignmentRecord(
    studentAssignment?.assignment,
    config
  );
}

export function isInstructionHubResult(studentAssignment, config) {
  return (
    config.isEnabled &&
    config.isResultsEnabled &&
    studentAssignment?.result?.[INSTRUCTION_HUB_ASSIGNMENT_FLAG] === true
  );
}

export function getInstructionHubAssignmentKey({
  encryptedUserProfileId,
  activityId,
  taskType
}) {
  return `${encryptedUserProfileId}_${activityId}_${taskType}`;
}

export function buildInstructionHubBlockedLookup(studentAssignments, config) {
  if (!config?.isEnabled || !Array.isArray(studentAssignments)) {
    return {};
  }

  return studentAssignments.reduce((blockedLookup, studentAssignment) => {
    if (
      isInstructionHubAssignment(studentAssignment, config) ||
      isInstructionHubResult(studentAssignment, config)
    ) {
      blockedLookup[getInstructionHubAssignmentKey(studentAssignment)] = true;
    }

    return blockedLookup;
  }, {});
}

export function isInstructionHubBlockedAssignment(
  studentAssignment,
  blockedLookup
) {
  return (
    blockedLookup[getInstructionHubAssignmentKey(studentAssignment)] === true
  );
}

export function isSelectableAssignment(studentAssignment, blockedLookup) {
  return (
    !studentAssignment.isHidden &&
    !isInstructionHubBlockedAssignment(studentAssignment, blockedLookup)
  );
}

export function getSelectedEditableAssignments(
  studentAssignments,
  blockedLookup
) {
  return (studentAssignments || []).filter(
    (studentAssignment) =>
      studentAssignment.isSelected &&
      !isInstructionHubBlockedAssignment(studentAssignment, blockedLookup)
  );
}

export function countInstructionHubBlockedAssignments(
  studentAssignments,
  blockedLookup
) {
  return studentAssignments.filter((studentAssignment) =>
    isInstructionHubBlockedAssignment(studentAssignment, blockedLookup)
  ).length;
}

export function isFullyInstructionHubBlocked(blockedCount, totalCount) {
  return totalCount > 0 && blockedCount === totalCount;
}

export function getInstructionHubAssignmentsConfig(
  flags = getInstructionHubFlags(),
  search = getWindowSearch()
) {
  const {
    isInstructionHubEnabled,
    isInstructionHubResultsEnabled,
    instructionHubManageUrl
  } = flags;

  return {
    isEnabled: isInstructionHubEnabled,
    isResultsEnabled: isInstructionHubResultsEnabled,
    deepLink: instructionHubManageUrl,
    shouldMarkAssignments:
      isInstructionHubEnabled &&
      shouldMarkInstructionHubAssignmentsFromQuery(search),
    shouldMarkResults:
      isInstructionHubEnabled &&
      shouldMarkInstructionHubResultsFromQuery(search)
  };
}

function markWithInstructionHubFlag(record) {
  return { ...record, [INSTRUCTION_HUB_ASSIGNMENT_FLAG]: true };
}

export function markInstructionHubAssignmentsForLocalTesting(
  assignments,
  config = getInstructionHubAssignmentsConfig()
) {
  const { shouldMarkAssignments, shouldMarkResults } = config;

  if (
    (!shouldMarkAssignments && !shouldMarkResults) ||
    !assignments ||
    !Array.isArray(assignments.studentAssignments)
  ) {
    return assignments;
  }

  return {
    ...assignments,
    studentAssignments: assignments.studentAssignments.map(
      (studentAssignment) => {
        const { assignment, result } = studentAssignment;

        return {
          ...studentAssignment,
          assignment:
            shouldMarkAssignments && assignment
              ? markWithInstructionHubFlag(assignment)
              : assignment,
          result:
            shouldMarkResults && result
              ? markWithInstructionHubFlag(result)
              : result
        };
      }
    )
  };
}

export function markInstructionHubStudentResultForLocalTesting(
  studentResult,
  config = getInstructionHubAssignmentsConfig()
) {
  if (
    !config.shouldMarkAssignments ||
    !studentResult ||
    !Array.isArray(studentResult.assignments)
  ) {
    return studentResult;
  }

  return {
    ...studentResult,
    assignments: studentResult.assignments.map(markWithInstructionHubFlag)
  };
}



// WEBPACK FOOTER //
// ./helpers/instruction-hub/instruction-hub-assignments.js